import React, { useState, useEffect } from "react";
import { PAGE_SIZE } from "../../../api";
import {
  fetchEntries,
  searchEntries,
  createEntry,
  updateEntry,
  deleteEntry,
} from "../services/KBService";
import Icon from "../../../components/Icon";
import Tooltip from "../../../components/Tooltip";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import Pagination from "../../../components/Pagination";
import Modal from "../../../components/Modal";

export const KB_CATEGORIES = {
  application: {
    label: "Application",
    color: "#7C3AED",
    bg: "#F5F3FF",
    border: "#DDD6FE",
    icon: "cpu",
  },
  database: {
    label: "Database",
    color: "#B45309",
    bg: "#FFFBEB",
    border: "#FDE68A",
    icon: "database",
  },
  network: {
    label: "Network",
    color: "#0369A1",
    bg: "#F0F9FF",
    border: "#BAE6FD",
    icon: "network",
  },
  infrastructure: {
    label: "Infrastructure",
    color: "#15803D",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    icon: "server",
  },
  other: {
    label: "Other",
    color: "#6B7280",
    bg: "#F9FAFB",
    border: "#E5E7EB",
    icon: "layers",
  },
};

export function getKBCategory(issueType) {
  const t = (issueType || "").toLowerCase();
  if (
    t.includes("app") ||
    t.includes("code") ||
    t.includes("deploy") ||
    t.includes("service") ||
    t.includes("api") ||
    t.includes("crash") ||
    t.includes("oom")
  )
    return "application";
  if (
    t.includes("db") ||
    t.includes("database") ||
    t.includes("sql") ||
    t.includes("mongo") ||
    t.includes("redis") ||
    t.includes("query")
  )
    return "database";
  if (
    t.includes("net") ||
    t.includes("dns") ||
    t.includes("tcp") ||
    t.includes("latency") ||
    t.includes("timeout") ||
    t.includes("firewall") ||
    t.includes("bandwidth")
  )
    return "network";
  if (
    t.includes("disk") ||
    t.includes("cpu") ||
    t.includes("memory") ||
    t.includes("server") ||
    t.includes("hardware") ||
    t.includes("storage") ||
    t.includes("infra") ||
    t.includes("host")
  )
    return "infrastructure";
  return "other";
}

export function KBModal({ entry, onClose, onSaved }) {
  const isEdit = !!entry?.id;
  const [form, setForm] = useState({
    title: entry?.title || "",
    issue_type: entry?.issue_type || "",
    description: entry?.description || "",
    root_cause: entry?.root_cause || "",
    resolution: entry?.resolution || "",
    is_global: entry?.is_global || false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit() {
    if (
      !form.title.trim() ||
      !form.issue_type.trim() ||
      !form.description.trim() ||
      !form.root_cause.trim() ||
      !form.resolution.trim()
    ) {
      setError("All fields marked * are required.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let saved;
      if (isEdit) {
        saved = await updateEntry(entry.id, {
          title: form.title,
          issue_type: form.issue_type,
          description: form.description,
          root_cause: form.root_cause,
          resolution: form.resolution,
        });
      } else {
        saved = await createEntry(form);
      }
      onSaved(saved, isEdit);
      onClose();
    } catch (e) {
      setError(`Failed to ${isEdit ? "update" : "create"} entry: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  const fields = [
    { key: "title", label: "Title", placeholder: "Short descriptive title" },
    {
      key: "issue_type",
      label: "Issue type",
      placeholder: "e.g. disk_full, oom_kill, network_timeout",
    },
    {
      key: "description",
      label: "Description",
      textarea: true,
      placeholder: "Describe when this issue occurs…",
    },
    {
      key: "root_cause",
      label: "Root cause",
      textarea: true,
      placeholder: "What causes this issue?",
    },
    {
      key: "resolution",
      label: "Resolution",
      textarea: true,
      placeholder: "Step-by-step fix…",
    },
  ];

  return (
    <Modal
      title={isEdit ? "Edit KB Entry" : "New KB Entry"}
      onClose={onClose}
      width={580}
    >
      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#FEF2F2",
            color: "#DC2626",
            padding: "10px 12px",
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 14,
            border: "1px solid #FECACA",
          }}
        >
          <Icon name="alert" size={14} color="#DC2626" />
          {error}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {fields.map((f) => (
          <div key={f.key}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 4,
              }}
            >
              {f.label} <span style={{ color: "#DC2626" }}>*</span>
            </label>
            {f.textarea ? (
              <textarea
                value={form[f.key]}
                onChange={(e) =>
                  setForm((p) => ({ ...p, [f.key]: e.target.value }))
                }
                placeholder={f.placeholder}
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #E5E7EB",
                  borderRadius: 7,
                  fontSize: 12,
                  boxSizing: "border-box",
                  resize: "vertical",
                  outline: "none",
                  fontFamily: "inherit",
                  lineHeight: 1.5,
                }}
              />
            ) : (
              <input
                value={form[f.key]}
                onChange={(e) =>
                  setForm((p) => ({ ...p, [f.key]: e.target.value }))
                }
                placeholder={f.placeholder}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #E5E7EB",
                  borderRadius: 7,
                  fontSize: 12,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            )}
          </div>
        ))}
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            padding: "10px 12px",
            background: "#F8FAFC",
            borderRadius: 7,
            border: "1px solid #E2E8F0",
          }}
        >
          <input
            type="checkbox"
            checked={form.is_global}
            onChange={(e) =>
              setForm((p) => ({ ...p, is_global: e.target.checked }))
            }
            style={{ width: 14, height: 14 }}
          />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
              Mark as global
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>
              Shared across all organizations
            </div>
          </div>
        </label>
      </div>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 18,
        }}
      >
        <button
          onClick={onClose}
          style={{
            padding: "8px 16px",
            border: "1px solid #E5E7EB",
            borderRadius: 7,
            background: "#fff",
            fontSize: 12,
            cursor: "pointer",
            color: "#374151",
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            padding: "8px 18px",
            background: loading ? "#93C5FD" : "#1D4ED8",
            color: "#fff",
            border: "none",
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Saving…" : isEdit ? "Save changes" : "Add entry"}
        </button>
      </div>
    </Modal>
  );
}

export function KBCard({ entry, onEdit, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const cat = KB_CATEGORIES[getKBCategory(entry.issue_type)];

  async function handleDelete() {
    if (!window.confirm(`Delete "${entry.title}"?`)) return;
    setDeleting(true);
    try {
      await deleteEntry(entry.id);
      onDelete(entry.id);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${cat.border}`,
        borderTop: `3px solid ${cat.color}`,
        borderRadius: 10,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <Tooltip text={`Category: ${cat.label}`}>
              <span
                style={{
                  fontSize: 10,
                  background: cat.bg,
                  color: cat.color,
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontWeight: 700,
                  border: `1px solid ${cat.border}`,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  cursor: "default",
                }}
              >
                <Icon name={cat.icon} size={10} color={cat.color} />
                {entry.issue_type}
              </span>
            </Tooltip>
            {entry.is_global && (
              <Tooltip text="Available to all organizations">
                <span
                  style={{
                    fontSize: 10,
                    background: "#EFF6FF",
                    color: "#1D4ED8",
                    padding: "2px 8px",
                    borderRadius: 10,
                    fontWeight: 700,
                    border: "1px solid #BFDBFE",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    cursor: "default",
                  }}
                >
                  <Icon name="globe" size={10} color="#1D4ED8" /> Global
                </span>
              </Tooltip>
            )}
          </div>
          <h3
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.4,
            }}
          >
            {entry.title}
          </h3>
        </div>
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          <button
            onClick={() => onEdit(entry)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 9px",
              border: "1px solid #E5E7EB",
              borderRadius: 6,
              background: "#fff",
              fontSize: 11,
              cursor: "pointer",
              color: "#374151",
              fontWeight: 500,
            }}
          >
            <Icon name="edit" size={11} color="#6B7280" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 9px",
              border: "1px solid #FECACA",
              borderRadius: 6,
              background: "#FEF2F2",
              fontSize: 11,
              cursor: deleting ? "not-allowed" : "pointer",
              color: "#DC2626",
              fontWeight: 500,
            }}
          >
            <Icon name="trash" size={11} color="#DC2626" />
            {deleting ? "…" : "Delete"}
          </button>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 12, color: "#6B7280", lineHeight: 1.6 }}>
        {entry.description}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div
          style={{
            background: "#FFFBEB",
            border: "1px solid #FDE68A",
            borderRadius: 7,
            padding: "9px 10px",
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#B45309",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}
          >
            Root cause
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 11,
              color: "#92400E",
              lineHeight: 1.5,
            }}
          >
            {entry.root_cause}
          </p>
        </div>
        <div
          style={{
            background: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 7,
            padding: "9px 10px",
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#15803D",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}
          >
            Resolution
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 11,
              color: "#166534",
              lineHeight: 1.5,
            }}
          >
            {entry.resolution}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function KnowledgeBaseView() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [activeScope, setActiveScope] = useState("all"); // all | global | org
  const [activeCat, setActiveCat] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchEntries(100)
      .then((data) => setEntries(data?.items || []))
      .catch(() => setError("Failed to load knowledge base."))
      .finally(() => setLoading(false));
  }, []);

  async function handleSearch() {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const results = await searchEntries({ query: searchQuery, top_k: 20 });
      setSearchResults(results || []);
      setPage(1);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setSearchResults(null);
    setSearchQuery("");
    setPage(1);
  }

  function handleSaved(saved, isEdit) {
    if (isEdit)
      setEntries((prev) => prev.map((e) => (e.id === saved.id ? saved : e)));
    else setEntries((prev) => [saved, ...prev]);
    setSearchResults(null);
    setPage(1);
  }

  function handleDelete(id) {
    setEntries((prev) => prev.filter((e) => e.id !== id));
    if (searchResults)
      setSearchResults((prev) => prev.filter((r) => r.entry.id !== id));
  }

  // Base entries (search or full list)
  const baseEntries = searchResults
    ? searchResults.map((r) => r.entry)
    : entries;

  // Filter by scope
  const scopeFiltered =
    activeScope === "global"
      ? baseEntries.filter((e) => e.is_global)
      : activeScope === "org"
        ? baseEntries.filter((e) => !e.is_global)
        : baseEntries;

  // Filter by category
  const catFiltered =
    activeCat === "all"
      ? scopeFiltered
      : scopeFiltered.filter((e) => getKBCategory(e.issue_type) === activeCat);

  // Count per category from scope-filtered
  const catCounts = Object.keys(KB_CATEGORIES).reduce((acc, k) => {
    acc[k] = scopeFiltered.filter(
      (e) => getKBCategory(e.issue_type) === k,
    ).length;
    return acc;
  }, {});

  // Pagination
  const paged = catFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Group search results by category
  const groupedSearch = searchResults
    ? catFiltered.reduce((acc, e) => {
        const c = getKBCategory(e.issue_type);
        if (!acc[c]) acc[c] = [];
        acc[c].push(e);
        return acc;
      }, {})
    : null;

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 14,
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Knowledge Base
            </h1>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
              {entries.length} {entries.length === 1 ? "entry" : "entries"} —
              runbooks, root causes, and resolutions
            </p>
          </div>
          {entries.length > 0 && (
            <button
              onClick={() => {
                setEditEntry(null);
                setShowModal(true);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "8px 16px",
                background: "#1D4ED8",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <Icon name="plus" size={14} color="#fff" /> Add entry
            </button>
          )}
        </div>

        {/* Search & Scope toggle (Toolbar) */}
        {entries.length > 0 && (
          <>
            {/* Search */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div
                style={{
                  flex: 1,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon
                  name="search"
                  size={14}
                  color="#9CA3AF"
                  style={{ position: "absolute", left: 10, pointerEvents: "none" }}
                />
                <input
                  placeholder="Search by symptom, error type, or issue…"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (!e.target.value) clearSearch();
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={{
                    width: "100%",
                    padding: "8px 10px 8px 30px",
                    border: "1px solid #E5E7EB",
                    borderRadius: 7,
                    fontSize: 12,
                    outline: "none",
                    color: "#111827",
                    background: "#fff",
                  }}
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={searching}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "8px 16px",
                  background: "#1D4ED8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: searching ? "not-allowed" : "pointer",
                }}
              >
                <Icon name="search" size={13} color="#fff" />
                {searching ? "Searching…" : "Search"}
              </button>
              {searchResults && (
                <button
                  onClick={clearSearch}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #E5E7EB",
                    borderRadius: 7,
                    background: "#fff",
                    fontSize: 12,
                    cursor: "pointer",
                    color: "#6B7280",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
            {searchResults && (
              <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 10 }}>
                {searchResults.length > 0 ? (
                  <>
                    <strong>{searchResults.length}</strong> results for "
                    <em>{searchQuery}</em>"
                  </>
                ) : (
                  <>
                    No results for "<em>{searchQuery}</em>"
                  </>
                )}
              </div>
            )}

            {/* Scope toggle */}
            <div
              style={{
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: "#9CA3AF",
                  marginRight: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Icon name="filter" size={12} color="#9CA3AF" />
                Scope:
              </span>
              {[
                { id: "all", label: "All", count: baseEntries.length },
                {
                  id: "global",
                  label: "Global",
                  count: baseEntries.filter((e) => e.is_global).length,
                  icon: "globe",
                },
                {
                  id: "org",
                  label: "Organization",
                  count: baseEntries.filter((e) => !e.is_global).length,
                  icon: "building",
                },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveScope(s.id);
                    setPage(1);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 10px",
                    border: `1px solid ${activeScope === s.id ? "#1D4ED8" : "#E5E7EB"}`,
                    borderRadius: 20,
                    background: activeScope === s.id ? "#EFF6FF" : "#fff",
                    color: activeScope === s.id ? "#1D4ED8" : "#6B7280",
                    fontSize: 11,
                    fontWeight: activeScope === s.id ? 700 : 400,
                    cursor: "pointer",
                  }}
                >
                  {s.icon && (
                    <Icon
                      name={s.icon}
                      size={11}
                      color={activeScope === s.id ? "#1D4ED8" : "#9CA3AF"}
                    />
                  )}
                  {s.label}
                  <span
                    style={{
                      background: activeScope === s.id ? "#BFDBFE" : "#F3F4F6",
                      color: activeScope === s.id ? "#1D4ED8" : "#9CA3AF",
                      padding: "0 5px",
                      borderRadius: 8,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {s.count}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Category sidebar */}
        {entries.length > 0 && (
          <div
            style={{
              width: 180,
              borderRight: "1px solid #E5E7EB",
              background: "#fff",
              padding: "14px 10px",
              flexShrink: 0,
              overflowY: "auto",
            }}
          >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: "#9CA3AF",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              padding: "2px 8px 8px",
            }}
          >
            Categories
          </div>
          <button
            onClick={() => {
              setActiveCat("all");
              setPage(1);
            }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              padding: "7px 10px",
              borderRadius: 7,
              border: "none",
              cursor: "pointer",
              background: activeCat === "all" ? "#EFF6FF" : "transparent",
              color: activeCat === "all" ? "#1D4ED8" : "#374151",
              fontSize: 12,
              fontWeight: activeCat === "all" ? 700 : 400,
              marginBottom: 2,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <Icon
                name="layers"
                size={13}
                color={activeCat === "all" ? "#1D4ED8" : "#9CA3AF"}
              />
              All
            </span>
            <span
              style={{
                fontSize: 10,
                background: activeCat === "all" ? "#BFDBFE" : "#F3F4F6",
                color: activeCat === "all" ? "#1D4ED8" : "#9CA3AF",
                padding: "1px 6px",
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              {scopeFiltered.length}
            </span>
          </button>
          {Object.entries(KB_CATEGORIES).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => {
                setActiveCat(key);
                setPage(1);
              }}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "7px 10px",
                borderRadius: 7,
                border: "none",
                cursor: "pointer",
                background: activeCat === key ? cat.bg : "transparent",
                color: activeCat === key ? cat.color : "#374151",
                fontSize: 12,
                fontWeight: activeCat === key ? 700 : 400,
                marginBottom: 2,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Icon
                  name={cat.icon}
                  size={13}
                  color={activeCat === key ? cat.color : "#9CA3AF"}
                />
                {cat.label}
              </span>
              <span
                style={{
                  fontSize: 10,
                  background: activeCat === key ? "#fff" : "#F3F4F6",
                  color: activeCat === key ? cat.color : "#9CA3AF",
                  padding: "1px 6px",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                {catCounts[key] || 0}
              </span>
            </button>
          ))}
        </div>
        )}

        {/* Card grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {error ? (
            <ErrorState message={error} />
          ) : loading ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))",
                gap: 12,
              }}
            >
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} height={180} style={{ borderRadius: 10 }} />
              ))}
            </div>
          ) : entries.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                padding: "40px 20px",
              }}
            >
              <EmptyState
                icon="book"
                title="No knowledge base entries"
                subtitle="Create runbooks, root causes, and resolutions to help resolve operational issues."
              />
              <div style={{ textAlign: "center", marginTop: -20, marginBottom: 12 }}>
                <button
                  onClick={() => {
                    setEditEntry(null);
                    setShowModal(true);
                  }}
                  style={{
                    padding: "8px 18px",
                    background: "#1D4ED8",
                    color: "#fff",
                    border: "none",
                    borderRadius: 7,
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Add Entry
                </button>
              </div>
            </div>
          ) : catFiltered.length === 0 ? (
            searchResults ? (
              <EmptyState
                icon="search"
                title="No results found"
                subtitle="No matching items were found for your search query. Try clearing the search or adjusting filters."
              />
            ) : (
              <EmptyState
                icon="book"
                title="No entries found"
                subtitle={
                  activeCat !== "all"
                    ? "No entries in this category match the selected scope."
                    : "No entries match the selected filters."
                }
              />
            )
          ) : (
            <>
              {searchResults && groupedSearch ? (
                // Grouped search results
                Object.entries(groupedSearch).map(([catKey, catEntries]) => {
                  const cat = KB_CATEGORIES[catKey];
                  return (
                    <div key={catKey} style={{ marginBottom: 20 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          marginBottom: 10,
                          paddingBottom: 6,
                          borderBottom: `2px solid ${cat.border}`,
                        }}
                      >
                        <Icon name={cat.icon} size={14} color={cat.color} />
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: cat.color,
                          }}
                        >
                          {cat.label}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            background: cat.bg,
                            color: cat.color,
                            padding: "1px 6px",
                            borderRadius: 8,
                            fontWeight: 600,
                            border: `1px solid ${cat.border}`,
                          }}
                        >
                          {catEntries.length}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(280px,1fr))",
                          gap: 12,
                        }}
                      >
                        {catEntries.map((entry) => (
                          <KBCard
                            key={entry.id}
                            entry={entry}
                            onEdit={(e) => {
                              setEditEntry(e);
                              setShowModal(true);
                            }}
                            onDelete={handleDelete}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(280px,1fr))",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    {paged.map((entry) => (
                      <KBCard
                        key={entry.id}
                        entry={entry}
                        onEdit={(e) => {
                          setEditEntry(e);
                          setShowModal(true);
                        }}
                        onDelete={handleDelete}
                      />
                    ))}
                  </div>
                  <Pagination
                    page={page}
                    total={catFiltered.length}
                    pageSize={PAGE_SIZE}
                    onChange={setPage}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <KBModal
          entry={editEntry}
          onClose={() => {
            setShowModal(false);
            setEditEntry(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
